import type { Session } from "@supabase/supabase-js";

export type AccountRole = "admin" | "provider" | "partner";

export type AccountRoleResolution =
  | {
      ok: true;
      role: AccountRole;
      roles: AccountRole[];
    }
  | {
      ok: false;
      reason: "multiple_roles";
      roles: AccountRole[];
    };

const ROLE_ORDER: AccountRole[] = ["admin", "provider", "partner"];

function metadataRoles(session: Session | null): AccountRole[] {
  const roles = session?.user.app_metadata?.roles;
  if (!Array.isArray(roles)) return [];
  const recognized = roles.filter((role): role is AccountRole =>
    ROLE_ORDER.includes(role as AccountRole)
  );
  return Array.from(new Set(recognized));
}

export function resolveAccountRole(session: Session | null): AccountRoleResolution | null {
  if (!session) return null;
  const roles = metadataRoles(session);
  if (roles.length > 1) {
    return { ok: false, reason: "multiple_roles", roles };
  }
  return {
    ok: true,
    role: roles[0] ?? "provider",
    roles
  };
}

export function accountRoleForSession(session: Session | null): AccountRole | null {
  const resolved = resolveAccountRole(session);
  return resolved?.ok ? resolved.role : null;
}

export function hasAdminRoleHint(session: Session | null): boolean {
  return accountRoleForSession(session) === "admin";
}

export function hasPartnerRoleHint(session: Session | null): boolean {
  return accountRoleForSession(session) === "partner";
}

export function hasRoleConflict(session: Session | null): boolean {
  const resolved = resolveAccountRole(session);
  return Boolean(resolved && !resolved.ok);
}
